// frontend/src/components/churches/AddEditChurchModal.tsx

import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { createChurch, updateChurch, Church } from '../../services/churchService';
import { useError } from '../../contexts/ErrorContext';

interface AddEditChurchModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  church: Church | null; // null for add, Church object for edit
}

export const AddEditChurchModal: React.FC<AddEditChurchModalProps> = ({
  show,
  onHide,
  onSuccess,
  church
}) => {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setError } = useError();

  useEffect(() => {
    if (church) {
      setName(church.name);
      setAddress(church.address || '');
    } else {
      setName('');
      setAddress('');
    }
  }, [church, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError(new Error('Church name is required'));
      return;
    }

    try {
      setIsSubmitting(true);

      if (church) {
        // Edit existing church
        await updateChurch(church.id, {
          name: name.trim(),
          address: address.trim() || null
        });
      } else {
        // Create new church
        await createChurch({
          name: name.trim(),
          address: address.trim() || null
        });
      }

      onSuccess();
      onHide();
    } catch (error) {
      setError(error instanceof Error ? error : new Error('Failed to save church'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setName('');
      setAddress('');
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{church ? 'Edit Church' : 'Add New Church'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>
              Church Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter church name"
              maxLength={100}
              required
              autoFocus
            />
            <Form.Text className="text-muted">
              Required. Maximum 100 characters.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Address</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Enter church address (optional)"
              maxLength={500}
            />
            <Form.Text className="text-muted">
              Optional. Maximum 500 characters.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : church ? 'Save Changes' : 'Create Church'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
